import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent, Over } from "@dnd-kit/core";
import type { FormFieldKind } from "@rwaforms/shared";
import BuilderTopBar from "../components/BuilderTopBar";
import FieldPalette from "../components/FieldPalette";
import FormCanvas from "../components/FormCanvas";
import PropertiesPanel from "../components/PropertiesPanel";
import { fetchFormDetail, fetchSession, updateDraftVersion, createDraftVersion } from "../lib/api-client";
import { applyBrandingTheme } from "../lib/theme";
import { useBuilderStore } from "../hooks/useBuilderStore";
import { useAutoSave } from "../hooks/useAutoSave";
import { useCollaborationClient } from "../hooks/useCollaborationClient";
import { validateFormSchema } from "@rwaforms/shared";

type DragItem =
  | { type: "palette-field"; fieldKind: FormFieldKind }
  | { type: "field"; fieldId: string; sectionId: string | null; index: number };

type DropTargetData =
  | { type: "field"; fieldId: string; sectionId: string | null; index: number }
  | { type: "section"; sectionId: string | null; insertIndex: number };

interface ParsedDropTarget {
  sectionId: string | null;
  index: number;
}

interface FormBuilderPageProps {
  mode?: "build" | "preview";
}

function FormBuilderPage({ mode = "build" }: FormBuilderPageProps): JSX.Element {
  const { formId, teamId } = useParams<{ formId: string; teamId: string }>();
  const navigate = useNavigate();
  const loadFromDetail = useBuilderStore((state) => state.loadFromDetail);
  const initialized = useBuilderStore((state) => state.initialized);
  const addField = useBuilderStore((state) => state.addField);
  const moveField = useBuilderStore((state) => state.moveField);
  const selectField = useBuilderStore((state) => state.selectField);
  const schema = useBuilderStore((state) => state.schema);
  const draftId = useBuilderStore((state) => state.draftId);
  const draftVersionId = useBuilderStore((state) => state.draftVersionId);
  const formTheme = useBuilderStore((state) => state.formTheme);
  const teamTheme = useBuilderStore((state) => state.teamTheme);
  const formTitle = useBuilderStore((state) => state.formTitle);
  const markDirty = useBuilderStore((state) => state.markDirty);
  const loadSchema = useBuilderStore((state) => state.loadSchema);
  const markSaved = useBuilderStore((state) => state.markSaved);
  const setSaving = useBuilderStore((state) => state.setSaving);

  const [activeDrag, setActiveDrag] = useState<DragItem | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });
  const [mobileActivePanel, setMobileActivePanel] = useState<"palette" | "properties" | null>(null);
  const selectedFieldId = useBuilderStore((state) => state.selectedFieldId);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  useEffect(() => {
    applyBrandingTheme(formTheme, teamTheme);
  }, [formTheme, teamTheme]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const formQuery = useQuery({
    queryKey: ["form", formId],
    queryFn: () => fetchFormDetail(formId!),
    enabled: Boolean(formId),
  });

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
  });

  const currentUser = sessionQuery.data?.session
    ? {
      userId: sessionQuery.data.session.userId,
      displayName: sessionQuery.data.session.email ?? sessionQuery.data.session.userId,
    }
    : null;

  const collaboration = useCollaborationClient({
    draftId,
    enabled: Boolean(draftId),
    user: currentUser,
  });

  useEffect(() => {
    if (formQuery.data) {
      loadFromDetail(formQuery.data);

      // Validate and redirect if URL teamId doesn't match form's actual teamId
      const actualTeamId = formQuery.data.form.teamId;
      if (teamId && actualTeamId && teamId !== actualTeamId) {
        // Redirect to the correct team URL
        navigate(`/teams/${actualTeamId}/forms/${formId}`, { replace: true });
      }
    }
  }, [formQuery.data, loadFromDetail, teamId, formId, navigate]);

  useAutoSave();

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("formId is not set");
      if (!draftVersionId) {
        return createDraftVersion(formId, schema);
      }
      return updateDraftVersion(draftVersionId, schema);
    },
    onMutate() {
      setSaving(true);
    },
    onSuccess(version) {
      loadSchema({
        id: version.id,
        formId: version.formId,
        versionId: version.id,
        schema: version.schema,
        logic: version.logic,
        settings: {},
        updatedAt: version.updatedAt,
      });
      markSaved();
    },
    onError() {
      setSaving(false);
    },
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (!ctrlOrCmd) return;

      // Ctrl/Cmd + S: Save draft
      if (e.key === "s" && !e.shiftKey) {
        e.preventDefault();
        if (formId && schema) {
          void saveDraftMutation.mutateAsync();
        }
        return;
      }

      // Ctrl/Cmd + Shift + S: Open sharing page
      if (e.key === "S" && e.shiftKey) {
        e.preventDefault();
        if (teamId && formId) {
          navigate(`/teams/${teamId}/forms/${formId}/sharing`);
        }
        return;
      }

      // Ctrl/Cmd + P: Publish (if form is valid)
      if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        if (!formId || !draftVersionId) return;

        // Validate form before publishing
        const validation = validateFormSchema(schema);
        if (!validation.valid) {
          const errorMessages = validation.issues
            .filter((issue) => issue.severity === "error")
            .map((issue) => issue.message)
            .join(", ");
          alert(`Form validation failed: ${errorMessages}`);
          return;
        }

        if (schema.fields.length === 0) {
          alert("Cannot publish form with no fields. Please add at least one field.");
          return;
        }

        if (!formTitle || formTitle.trim().length === 0) {
          alert("Please set a form title before publishing.");
          return;
        }

        // Navigate to a page that can trigger publish, or trigger it directly
        // For now, we'll just show an alert that they should use the Publish button
        // In a real implementation, you might want to expose a publish function from BuilderTopBar
        alert("Please use the Publish button in the top bar to publish your form.");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formId, teamId, schema, draftVersionId, formTitle, navigate, saveDraftMutation]);

  const activeField = useMemo(() => {
    if (activeDrag?.type === "field") {
      return schema.fields.find((field) => field.id === activeDrag.fieldId) ?? null;
    }
    return null;
  }, [activeDrag, schema.fields]);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (isDragItem(data)) {
      setActiveDrag(data);
    } else {
      console.warn("[FormBuilderPage] Invalid drag item data:", data);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!active || !over) return;

    const activeData = active.data.current;
    if (!isDragItem(activeData)) {
      console.warn("[FormBuilderPage] Invalid active drag data:", activeData);
      return;
    }

    const overData = parseOverData(over);
    if (!overData) {
      console.warn("[FormBuilderPage] Invalid drop target data:", over.data.current);
      return;
    }

    // Validate drop operation
    if (!validateDropOperation(activeData, overData, schema)) {
      console.warn("[FormBuilderPage] Invalid drop operation prevented");
      return;
    }

    if (activeData.type === "palette-field") {
      const created = addField(
        activeData.fieldKind,
        overData.sectionId,
        Number.isFinite(overData.index) && overData.index >= 0 ? overData.index : undefined
      );
      if (created) {
        selectField(created.id);
      }
      return;
    }

    if (activeData.type === "field") {
      // Prevent dropping field on itself at same position
      if (activeData.sectionId === overData.sectionId && activeData.index === overData.index) {
        return;
      }
      moveField(activeData.fieldId, overData.sectionId, Math.max(0, overData.index));
      selectField(activeData.fieldId);
    }
  };

  if (!formId) {
    return <div style={{ padding: "2rem" }}>Form not specified.</div>;
  }

  if (formQuery.isLoading && !initialized) {
    return <div style={{ padding: "2rem" }}>Loading builder...</div>;
  }

  if (formQuery.error) {
    return (
      <div style={{ padding: "2rem", color: "var(--color-critical)" }}>
        Unable to load this form. Please verify access and try again.
      </div>
    );
  }

  return (
    <div className="app-shell">
      <BuilderTopBar
        collaborators={collaboration.collaborators}
        connectionStatus={isOnline ? collaboration.status : "disconnected"}
      />
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="app-content">
          <div className={`app-pane app-pane--palette ${mobileActivePanel === "palette" ? "is-open-mobile" : ""}`}>
            <FieldPalette />
          </div>
          <div className="app-pane app-pane--canvas">
            <FormCanvas mode={mode} />
          </div>
          <div className={`app-pane app-pane--properties ${mobileActivePanel === "properties" ? "is-open-mobile" : ""}`}>
            <div className="surface scroll-container" style={{ padding: "1.25rem" }}>
              <PropertiesPanel />
            </div>
          </div>
        </div>
        <DragOverlay
          style={{
            cursor: "grabbing",
          }}
        >
          {activeDrag ? (
            <div
              className="surface"
              style={{
                padding: "0.9rem 1rem",
                borderRadius: "var(--radius-base)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-md)",
                minWidth: "200px",
                fontSize: "0.95rem",
                background: "var(--color-surface)",
                opacity: 0.9,
                transform: "rotate(2deg)",
              }}
            >
              {activeDrag.type === "palette-field"
                ? `New ${activeDrag.fieldKind.replace("_", " ")}`
                : activeField?.title ?? "Field"}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Mobile Panel Backdrop */}
      {mobileActivePanel && (
        <div
          className="mobile-panel-backdrop"
          onClick={() => setMobileActivePanel(null)}
        />
      )}

      {/* Mobile Toolbar */}
      <div className="mobile-builder-toolbar mobile-only">
        <button
          type="button"
          className={`button ${mobileActivePanel === "palette" ? "primary" : "secondary"}`}
          onClick={() => setMobileActivePanel(prev => prev === "palette" ? null : "palette")}
        >
          ➕ Add Field
        </button>
        <button
          type="button"
          className={`button ${mobileActivePanel === "properties" ? "primary" : "secondary"}`}
          disabled={!selectedFieldId}
          style={{ opacity: !selectedFieldId ? 0.5 : 1 }}
          onClick={() => {
            if (selectedFieldId) {
              setMobileActivePanel(prev => prev === "properties" ? null : "properties");
            }
          }}
        >
          ✏️ Edit Field
        </button>
      </div>
    </div>
  );
}

/**
 * Type guard for DragItem
 */
function isDragItem(data: unknown): data is DragItem {
  if (!data || typeof data !== "object") return false;
  const item = data as Record<string, unknown>;
  if (item.type === "palette-field") {
    return typeof item.fieldKind === "string";
  }
  if (item.type === "field") {
    return (
      typeof item.fieldId === "string" &&
      (item.sectionId === null || typeof item.sectionId === "string") &&
      typeof item.index === "number"
    );
  }
  return false;
}

/**
 * Type guard for DropTargetData
 */
function isDropTargetData(data: unknown): data is DropTargetData {
  if (!data || typeof data !== "object") return false;
  const item = data as Record<string, unknown>;
  if (item.type === "field") {
    return (
      typeof item.fieldId === "string" &&
      (item.sectionId === null || typeof item.sectionId === "string") &&
      typeof item.index === "number"
    );
  }
  if (item.type === "section") {
    return (
      (item.sectionId === null || typeof item.sectionId === "string") &&
      typeof item.insertIndex === "number"
    );
  }
  return false;
}

/**
 * Parse and validate drop target data with comprehensive validation
 */
function parseOverData(over: Over | null): ParsedDropTarget | null {
  if (!over) return null;

  const data = over.data.current;
  if (!isDropTargetData(data)) {
    console.warn("[parseOverData] Invalid drop target data structure:", data);
    return null;
  }

  let sectionId: string | null = null;
  let index: number = 0;

  if (data.type === "field") {
    sectionId = data.sectionId ?? null;
    index = typeof data.index === "number" && !Number.isNaN(data.index) ? Math.max(0, data.index) : 0;
  } else if (data.type === "section") {
    sectionId = data.sectionId ?? null;
    index = typeof data.insertIndex === "number" && !Number.isNaN(data.insertIndex) ? Math.max(0, data.insertIndex) : 0;
  }

  return { sectionId, index };
}

/**
 * Validate that a drop operation is allowed
 */
function validateDropOperation(
  activeData: DragItem,
  overData: ParsedDropTarget,
  schema: ReturnType<typeof useBuilderStore.getState>["schema"]
): boolean {
  // Validate section exists if specified
  if (overData.sectionId !== null) {
    const sectionExists = schema.sections.some((s) => s.id === overData.sectionId);
    if (!sectionExists) {
      return false;
    }
  }

  // If dragging a field, validate it exists
  if (activeData.type === "field") {
    const fieldExists = schema.fields.some((f) => f.id === activeData.fieldId);
    if (!fieldExists) {
      return false;
    }
  }

  // Validate index is within reasonable bounds
  if (overData.index < 0 || overData.index > 1000) {
    return false;
  }

  return true;
}

export default FormBuilderPage;
